import { useState, useEffect } from "react";
import { Outlet, NavLink, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { useLanguage } from "../../context/LanguageContext";
import { useTheme } from "../../context/ThemeContext";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { ScrollArea } from "../ui/scroll-area";
import { cn, API_URL } from "../../lib/utils";
import { PinDialog } from "../auth/PinDialog";
import axios from "axios";
import { InstallAppButton } from "../pwa/InstallAppButton";
import { WelcomeSplash } from "../pwa/WelcomeSplash";
import {
  LayoutDashboard,
  Info,
  AlertTriangle,
  CheckCircle2,
  ShoppingCart,
  Grid3X3,
  ClipboardList,
  Package,
  Warehouse,
  Users,
  Settings,
  LogOut,
  Sun,
  Moon,
  ChefHat,
  Languages,
  Menu,
  X,
  FileText,
  BarChart3,
  CalendarDays,
  Wallet,
  ShieldCheck,
  FileSpreadsheet,
  Truck,
  ShoppingBag,
  ClipboardCheck,
  Landmark,
  UsersRound,
  BadgeDollarSign,
  Target,
  Tag,
  FileSearch,
  Boxes,
  Factory,
  Send,
  Car,
  Wrench,
  FolderKanban,
  TrendingUp,
  Building2,
  Lock,
  Gift,
  Repeat,
  MonitorPlay,
  HeartHandshake,
  Crown,
  KeyRound,
} from "lucide-react";

const MainLayout = () => {
  const { user, logout, isManager, isAdmin, hasModule, deviceUnlocked, switchUser, refreshUser } = useAuth();
  const { token } = useAuth();
  const [pinOpen, setPinOpen] = useState(false);
  const handleSwitchUser = async () => { await switchUser(); navigate("/perfis", { replace: true }); };
  const handleLockDevice = async () => { navigate("/login?classic=1", { replace: true }); await logout(true); };
  const [platformMsgs, setPlatformMsgs] = useState([]);

  useEffect(() => {
    if (!user) return;
    axios.get(`${API_URL}/notifications/platform`, { withCredentials: true })
      .then((res) => setPlatformMsgs(res.data || []))
      .catch(() => {});
  }, [user?.user_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const dismissMsg = async (id) => {
    setPlatformMsgs((p) => p.filter((m) => m.message_id !== id));
    try {
      await axios.post(`${API_URL}/notifications/platform/${id}/dismiss`, {}, { headers: { Authorization: `Bearer ${token}` }, withCredentials: true });
    } catch {}
  };
  const { t, language, setLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Verificar se é utilizador da cozinha
  const isKitchenUser = user?.role === "kitchen";

  // Close sidebar on route change (mobile)
  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  // Se for utilizador da cozinha, redirecionar para /kitchen
  useEffect(() => {
    if (isKitchenUser && location.pathname !== "/kitchen") {
      window.location.href = "/kitchen";
    }
  }, [isKitchenUser, location.pathname]);

  // Menu simplificado para utilizador da cozinha
  const kitchenNavItems = [
    { path: "/kitchen", label: "Cozinha", icon: ChefHat },
  ];

  const allSections = [
    {
      title: "Vendas",
      items: [
        { path: "/dashboard", label: t("dashboard"), icon: LayoutDashboard, module: "dashboard" },
        { path: "/tables", label: t("tables"), icon: Grid3X3, module: "tables" },
        { path: "/orders", label: t("orders"), icon: ClipboardList, module: "orders" },
        { path: "/quotes", label: "Orçamentos", icon: FileText, module: "quotes" },
        { path: "/kitchen", label: "Cozinha", icon: ChefHat, module: "kitchen" },
        { path: "/invoices", label: t("invoices"), icon: FileText, module: "invoices" },
        { path: "/cash", label: "Caixa", icon: Wallet, module: "cash" },
        { path: "/reservations", label: "Reservas", icon: CalendarDays, module: "reservations" },
        { path: "/crm", label: "CRM", icon: Target, module: "crm" },
        { path: "/pricing", label: "Preços & Promoções", icon: Tag, module: "pricing" },
        { path: "/recurring", label: "Recorrências", icon: Repeat, module: "recurring" },
        { path: "/giftcards", label: "Gift Cards", icon: Gift, module: "giftcards" },
      ],
    },
    {
      title: "Compras",
      items: [
        { path: "/suppliers", label: "Fornecedores", icon: Truck, module: "suppliers" },
        { path: "/purchases", label: "Compras", icon: ShoppingBag, module: "purchases" },
        { path: "/requisitions", label: "Requisições", icon: FileSearch, module: "requisitions" },
      ],
    },
    {
      title: "Stock",
      items: [
        { path: "/products", label: t("products"), icon: Package, module: "products" },
        { path: "/stock", label: t("stock"), icon: Warehouse, module: "stock" },
        { path: "/inventory", label: "Inventário", icon: ClipboardCheck, module: "inventory" },
        { path: "/warehouses", label: "Armazéns", icon: Warehouse, module: "warehouses" },
        { path: "/lots", label: "Lotes & Validades", icon: Boxes, module: "lots" },
        { path: "/production", label: "Produção", icon: Factory, module: "production" },
      ],
    },
    {
      title: "Logística",
      items: [
        { path: "/logistics", label: "Expedições", icon: Send, module: "logistics" },
        { path: "/fleet", label: "Frota", icon: Car, module: "fleet" },
        { path: "/maintenance", label: "Manutenção", icon: Wrench, module: "maintenance" },
      ],
    },
    {
      title: "Gestão",
      items: [
        { path: "/finance", label: "Financeiro", icon: Landmark, module: "finance" },
        { path: "/banks", label: "Bancos", icon: Landmark, module: "banks" },
        { path: "/customers", label: "Clientes", icon: UsersRound, module: "customers" },
        { path: "/employees", label: t("employees"), icon: Users, module: "employees" },
        { path: "/hr", label: "RH", icon: BadgeDollarSign, module: "hr" },
        { path: "/assets", label: "Ativos", icon: Grid3X3, module: "assets" },
        { path: "/projects", label: "Projetos", icon: FolderKanban, module: "projects" },
        { path: "/insurers", label: "Seguradoras", icon: HeartHandshake, module: "insurers" },
      ],
    },
    {
      title: "Análise",
      items: [
        { path: "/bi", label: "BI Executivo", icon: TrendingUp, module: "bi" },
        { path: "/report-center", label: "Gerar Relatórios", icon: FileText, module: "reports" },
        { path: "/reports", label: t("reports"), icon: BarChart3, module: "reports" },
        { path: "/accounting", label: "Contabilidade", icon: FileSpreadsheet, module: "accounting" },
        { path: "/saft", label: "SAF-T (AGT)", icon: FileSpreadsheet, module: "saft" },
      ],
    },
    {
      title: "Sistema",
      items: [
        { path: "/organization", label: "Organização", icon: Building2, module: "organization" },
        { path: "/security", label: "Segurança", icon: Lock, module: "security" },
        { path: "/adscreen", label: "Publicidade no Ecrã", icon: MonitorPlay, module: "adscreen" },
        { path: "/plan", label: "O Meu Plano", icon: Crown, module: "plan" },
        { path: "/admin", label: "Admin", icon: ShieldCheck, module: "admin", adminOnly: true },
        { path: "/audit", label: "Auditoria", icon: ShieldCheck, module: "audit", adminOnly: true },
        { path: "/settings", label: t("settings"), icon: Settings, module: "settings" },
      ],
    },
  ];

  const navSections = allSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => {
        if (item.adminOnly) return isAdmin;
        return hasModule(item.module);
      }),
    }))
    .filter((section) => section.items.length > 0);

  const fullNavItems = navSections.flatMap((s) => s.items);

  // Usar menu da cozinha ou menu completo
  const navItems = isKitchenUser ? kitchenNavItems : fullNavItems;
  const sections = isKitchenUser ? [{ title: null, items: kitchenNavItems }] : navSections;

  const languages = [
    { code: "pt", label: "Português" },
    { code: "en", label: "English" },
    { code: "es", label: "Español" },
  ];

  // Guard: bloquear acesso direto por URL a módulos sem permissão
  const pathModule = {};
  allSections.forEach((s) => s.items.forEach((i) => { pathModule[i.path] = i; }));
  const currentItem = pathModule["/" + location.pathname.split("/")[1]];
  if (!isKitchenUser && currentItem) {
    const allowed = currentItem.adminOnly ? isAdmin : hasModule(currentItem.module);
    if (!allowed) {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return (
    <div className="min-h-screen bg-background flex">
      <WelcomeSplash user={user} />
      <PinDialog open={pinOpen} onOpenChange={setPinOpen} mode="own" hasPin={!!user?.has_pin} onSaved={refreshUser} />
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex w-64 flex-col border-r border-border bg-card fixed h-screen">
        {/* Logo */}
        <div className="h-16 flex items-center gap-3 px-6 border-b border-border">
          <img src="/lumora-logo.png" alt="Lumora" className="w-9 h-9" data-testid="sidebar-logo" />
          <span className="font-bold text-lg font-['Outfit']">Lumora</span>
        </div>

        {/* Navigation */}
        <ScrollArea className="flex-1 py-4">
          <nav className="px-3 space-y-3">
            {sections.map((section, si) => (
              <div key={si}>
                {section.title && (
                  <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">{section.title}</p>
                )}
                <div className="space-y-0.5">
                  {section.items.map((item) => (
                    <NavLink
                      key={item.path}
                      to={item.path}
                      className={({ isActive }) =>
                        cn(
                          "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                          isActive
                            ? "bg-primary text-primary-foreground"
                            : "text-muted-foreground hover:bg-muted hover:text-foreground"
                        )
                      }
                      data-testid={`nav-${item.path.slice(1)}`}
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ))}
          </nav>
        </ScrollArea>

        {/* User Section */}
        <div className="p-4 border-t border-border">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 w-full p-2 rounded-lg hover:bg-muted transition-colors" data-testid="user-menu">
                <Avatar className="w-9 h-9">
                  <AvatarImage src={user?.picture} />
                  <AvatarFallback className="bg-primary text-primary-foreground">
                    {user?.name?.charAt(0) || "U"}
                  </AvatarFallback>
                </Avatar>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground capitalize">{user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={toggleTheme}>
                {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                {theme === "dark" ? "Tema Claro" : "Tema Escuro"}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuLabel className="text-xs text-muted-foreground">Idioma</DropdownMenuLabel>
              {languages.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  className={language === lang.code ? "bg-muted" : ""}
                >
                  {lang.label}
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <InstallAppButton asMenuItem />
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setPinOpen(true)} data-testid="my-pin-btn">
                <KeyRound className="w-4 h-4 mr-2" />
                {user?.has_pin ? "Alterar o meu PIN" : "Definir o meu PIN"}
              </DropdownMenuItem>
              {deviceUnlocked && (
                <DropdownMenuItem onClick={handleSwitchUser} data-testid="switch-user-btn">
                  <Users className="w-4 h-4 mr-2" />
                  Trocar utilizador
                </DropdownMenuItem>
              )}
              {deviceUnlocked && isManager && (
                <DropdownMenuItem onClick={handleLockDevice} className="text-destructive" data-testid="lock-device-menu-btn">
                  <Lock className="w-4 h-4 mr-2" />
                  Bloquear dispositivo
                </DropdownMenuItem>
              )}
              {!deviceUnlocked && (
                <DropdownMenuItem onClick={() => logout()} className="text-destructive" data-testid="logout-btn">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t("logout")}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-card border-b border-border z-50 flex items-center justify-between px-4">
        <div className="flex items-center gap-3">
          <img src="/lumora-logo.png" alt="Lumora" className="w-8 h-8" />
          <span className="font-bold font-['Outfit']">Lumora</span>
        </div>
        <Button variant="ghost" size="icon" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      {/* Mobile Sidebar */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <aside className="absolute left-0 top-16 bottom-0 w-64 bg-card border-r border-border animate-fade-in">
            <ScrollArea className="h-full py-4">
              <nav className="px-3 space-y-3">
                {sections.map((section, si) => (
                  <div key={si}>
                    {section.title && (
                      <p className="px-3 pb-1 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">{section.title}</p>
                    )}
                    <div className="space-y-0.5">
                      {section.items.map((item) => (
                        <NavLink
                          key={item.path}
                          to={item.path}
                          className={({ isActive }) =>
                            cn(
                              "flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all",
                              isActive
                                ? "bg-primary text-primary-foreground"
                                : "text-muted-foreground hover:bg-muted hover:text-foreground"
                            )
                          }
                        >
                          <item.icon className="w-4 h-4" />
                          {item.label}
                        </NavLink>
                      ))}
                    </div>
                  </div>
                ))}
              </nav>

              <div className="px-3 mt-4 pt-4 border-t border-border">
                <Button variant="ghost" className="w-full justify-start" onClick={toggleTheme}>
                  {theme === "dark" ? <Sun className="w-4 h-4 mr-2" /> : <Moon className="w-4 h-4 mr-2" />}
                  {theme === "dark" ? "Tema Claro" : "Tema Escuro"}
                </Button>
                {deviceUnlocked ? (
                  <Button variant="ghost" className="w-full justify-start" onClick={handleSwitchUser} data-testid="mobile-switch-user-btn">
                    <Users className="w-4 h-4 mr-2" />
                    Trocar utilizador
                  </Button>
                ) : (
                  <Button variant="ghost" className="w-full justify-start text-destructive" onClick={() => logout()} data-testid="mobile-logout-btn">
                    <LogOut className="w-4 h-4 mr-2" />
                    {t("logout")}
                  </Button>
                )}
              </div>
            </ScrollArea>
          </aside>
        </div>
      )}

      {/* Mobile Bottom Navigation */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 h-16 bg-card border-t border-border z-50 flex justify-around items-center">
        {navItems.slice(0, 5).map((item) => (
          <NavLink
            key={item.path}
            to={item.path}
            className={({ isActive }) =>
              cn(
                "flex flex-col items-center gap-1 p-2 rounded-lg transition-all",
                isActive ? "text-primary" : "text-muted-foreground"
              )
            }
          >
            <item.icon className="w-5 h-5" />
            <span className="text-xs">{item.label}</span>
          </NavLink>
        ))}
      </nav>

      {/* Botão flutuante POS — abre na mesma aba (o POS tem botão para voltar) */}
      {!isKitchenUser && hasModule("pos") && (
        <button
          onClick={() => navigate("/pos")}
          className="fixed bottom-20 lg:bottom-8 right-5 lg:right-8 z-50 group flex items-center justify-center w-16 h-16 rounded-full bg-primary text-primary-foreground shadow-xl shadow-primary/40 hover:scale-110 active:scale-95 transition-transform"
          data-testid="pos-fab"
          aria-label="Abrir POS"
          title="Abrir POS"
        >
          <ShoppingCart className="w-7 h-7" />
          <span className="absolute right-full mr-3 whitespace-nowrap rounded-lg bg-foreground text-background text-xs font-semibold px-3 py-1.5 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Abrir POS
          </span>
        </button>
      )}

      {/* Main Content */}
      <main className="flex-1 min-w-0 lg:ml-64">
        <div className="min-h-screen pt-16 lg:pt-0 pb-20 lg:pb-0">
          <div className="p-3 sm:p-4 lg:p-6">
            {platformMsgs.map((m) => (
              <div
                key={m.message_id}
                className={cn(
                  "mb-4 flex items-start justify-between gap-3 rounded-lg border px-4 py-3 text-sm",
                  m.type === "success" ? "border-emerald-500/40 bg-emerald-500/10" : m.type === "warning" ? "border-amber-500/40 bg-amber-500/10" : "border-blue-500/40 bg-blue-500/10"
                )}
                data-testid={`platform-message-${m.message_id}`}
              >
                <div className="flex items-start gap-2">
                  {m.type === "success" ? (
                    <CheckCircle2 className="w-4 h-4 mt-0.5 text-emerald-500 shrink-0" />
                  ) : m.type === "warning" ? (
                    <AlertTriangle className="w-4 h-4 mt-0.5 text-amber-500 shrink-0" />
                  ) : (
                    <Info className="w-4 h-4 mt-0.5 text-blue-500 shrink-0" />
                  )}
                  <span><strong>{m.title}</strong>{m.body ? <> — {m.body}</> : null}</span>
                </div>
                <button onClick={() => dismissMsg(m.message_id)} className="shrink-0 opacity-60 hover:opacity-100" data-testid={`dismiss-message-${m.message_id}`} aria-label="Dispensar mensagem">
                  <X className="w-4 h-4" />
                </button>
              </div>
            ))}
            {(user?.plan_status === "expired" || user?.plan_status === "suspended") && (
              <div className="mb-4 flex items-center justify-between flex-wrap gap-2 rounded-lg border border-red-500/50 bg-red-500/10 px-4 py-3 text-sm" data-testid="expired-banner">
                <span>
                  {user.plan_status === "suspended" ? (
                    <>A subscrição da sua empresa está <strong>suspensa</strong>.</>
                  ) : (
                    <>A sua <strong>subscrição terminou</strong>.</>
                  )}{" "}
                  Não é possível registar novas vendas. Insira a <strong>chave de ativação</strong> na página "O Meu Plano" ou contacte <strong>suporte@lumora.ao</strong>.
                </span>
                {hasModule("plan") && (
                  <NavLink to="/plan" className="font-semibold text-red-600 hover:underline" data-testid="expired-banner-plan-link">
                    Ver plano →
                  </NavLink>
                )}
              </div>
            )}
            {user?.plan_status === "trial" && (
              <div className="mb-4 flex items-center justify-between flex-wrap gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm" data-testid="trial-banner">
                <span>
                  Plano <strong>{user.plan === "bronze" ? "LUMORA Bronze" : "LUMORA Ouro"}</strong> — período de teste:{" "}
                  <strong>{user.trial_days_left}</strong> dia(s) restante(s)
                </span>
                {hasModule("plan") && (
                  <NavLink to="/plan" className="font-semibold text-amber-600 hover:underline" data-testid="trial-banner-plan-link">
                    Gerir plano →
                  </NavLink>
                )}
              </div>
            )}
            <Outlet />
          </div>
        </div>
      </main>
    </div>
  );
};

export default MainLayout;
